/**
 * Plan 254 (#398): the NDJSON transport for a streamed chat reply.
 *
 * `POST /api/v1/chat/sessions/{id}/messages` with
 * `Accept: application/x-ndjson` answers one JSON object per line:
 * `started`, `delta`, `citation`, `ping`, then `done` or `error`. axios cannot
 * read a streamed body in a browser, so this uses `fetch`.
 *
 * The contract with the call sites:
 *  - it resolves with the same `SendChatMessageResponse` as `sendChatMessage`
 *    (from `done`, or from a plain JSON answer when the backend's kill switch
 *    is on or the turn is not eligible to stream);
 *  - a refusal before the stream rejects with an AxiosError-shaped error,
 *    with `data.detail` mirrored as the #792 interceptor does, so the
 *    existing catch blocks (403 security copy, 429 cap notice) keep working;
 *  - a stream that stops after `started` without a terminal event -- a lost
 *    connection, or no event for `CHAT_STREAM_IDLE_TIMEOUT_MS` -- rejects as
 *    a request timeout. The server saves the reply regardless, so the #1236
 *    recovery (`isRequestTimeout` + `awaitPersistedChatReply`) finds it.
 */
import type { SendChatMessageResponse } from './chatApi';

/**
 * No event of any kind for this long means the connection is dead, not that
 * the reply is slow: the server sends a `ping` every 15 s while it waits.
 */
export const CHAT_STREAM_IDLE_TIMEOUT_MS = 45_000;

export interface ChatStreamHandlers {
  /** The saved user row. Replace the optimistic bubble with it. */
  onStarted?: (userMessage: SendChatMessageResponse['user_message']) => void;
  /** Reply text; every citation marker in it is already `[N]`. */
  onDelta?: (text: string) => void;
  /** Metadata for `[N]`, delivered before the first delta that shows it. */
  onCitation?: (citation: any) => void;
}

export interface StreamChatSendOptions {
  url: string;
  body: Record<string, unknown>;
  handlers?: ChatStreamHandlers;
  fetchImpl?: typeof fetch;
  /** Resolves to `Bearer …` or null. MUST NOT call supabase getSession (#565). */
  authHeader: () => Promise<string | null>;
  /** The shared 401 sign-out check, given the header that was rejected. */
  onUnauthorized?: (failedAuthHeader: string | null) => void;
  idleTimeoutMs?: number;
}

type StreamEvent =
  | { type: 'started'; user_message: SendChatMessageResponse['user_message'] }
  | { type: 'delta'; text: string }
  | { type: 'citation'; citation: any }
  | { type: 'ping' }
  | { type: 'done'; response: SendChatMessageResponse }
  | { type: 'error'; status: number; message: string; request_id?: string };

/** Splits a byte/text stream into parsed JSON lines. */
export function createNdjsonDecoder(onLine: (obj: any) => void) {
  const textDecoder = new TextDecoder();
  let buffer = '';

  const drain = (final: boolean) => {
    let newline = buffer.indexOf('\n');
    while (newline !== -1) {
      const raw = buffer.slice(0, newline).trim();
      buffer = buffer.slice(newline + 1);
      if (raw) onLine(JSON.parse(raw));
      newline = buffer.indexOf('\n');
    }
    if (final && buffer.trim()) {
      const raw = buffer.trim();
      buffer = '';
      onLine(JSON.parse(raw));
    }
  };

  return {
    push(chunk: Uint8Array | string) {
      buffer += typeof chunk === 'string' ? chunk : textDecoder.decode(chunk, { stream: true });
      drain(false);
    },
    end() {
      buffer += textDecoder.decode();
      drain(true);
    },
  };
}

/** An error that `isRequestTimeout` recognises, as an axios timeout would be. */
function timeoutError(reason: string): Error {
  const err = new Error(`Chat stream stopped: ${reason}`) as Error & { code: string };
  err.code = 'ETIMEDOUT';
  return err;
}

/** An error in the AxiosError shape the chat call sites already handle. */
function httpError(status: number, data: any): Error {
  const err = new Error(`Request failed with status code ${status}`) as Error & {
    response: { status: number; data: any };
  };
  // Issue #792: mirror error.message into data.detail when detail is absent.
  if (
    data &&
    typeof data === 'object' &&
    data.detail === undefined &&
    data.error &&
    typeof data.error.message === 'string'
  ) {
    data.detail = data.error.message;
  }
  err.response = { status, data };
  return err;
}

async function readJson(response: Response): Promise<any> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

export async function streamChatSend({
  url,
  body,
  handlers = {},
  fetchImpl = fetch,
  authHeader,
  onUnauthorized,
  idleTimeoutMs = CHAT_STREAM_IDLE_TIMEOUT_MS,
}: StreamChatSendOptions): Promise<SendChatMessageResponse> {
  const controller = new AbortController();
  let timedOut = false;
  let idleTimer: ReturnType<typeof setTimeout> | undefined;
  const armIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, idleTimeoutMs);
  };

  const authorization = await authHeader();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/x-ndjson',
  };
  if (authorization) headers.Authorization = authorization;

  armIdleTimer();
  try {
    let response: Response;
    try {
      response = await fetchImpl(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (e) {
      if (timedOut) throw timeoutError('no response');
      throw e; // never reached the server: an ordinary network error
    }

    if (!response.ok) {
      const data = await readJson(response);
      if (response.status === 401) onUnauthorized?.(authorization);
      throw httpError(response.status, data);
    }

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/x-ndjson')) {
      return (await response.json()) as SendChatMessageResponse;
    }

    let result: SendChatMessageResponse | undefined;
    let failure: Error | undefined;
    const decoder = createNdjsonDecoder((event: StreamEvent) => {
      switch (event.type) {
        case 'started':
          handlers.onStarted?.(event.user_message);
          break;
        case 'delta':
          handlers.onDelta?.(event.text);
          break;
        case 'citation':
          handlers.onCitation?.(event.citation);
          break;
        case 'done':
          result = event.response;
          break;
        case 'error':
          failure = httpError(event.status, {
            detail: event.message,
            error: { message: event.message, request_id: event.request_id },
          });
          break;
        default:
          break; // ping, or an event type this client does not know
      }
    });

    const reader = response.body!.getReader();
    // Browsers reject a pending read on abort; do not depend on it.
    const aborted = new Promise<never>((_, reject) => {
      controller.signal.addEventListener('abort', () => reject(new Error('aborted')));
    });
    aborted.catch(() => undefined);
    try {
      while (!result && !failure) {
        let chunk: ReadableStreamReadResult<Uint8Array>;
        try {
          chunk = await Promise.race([reader.read(), aborted]);
        } catch {
          throw timeoutError(timedOut ? 'idle timeout' : 'connection lost');
        }
        armIdleTimer();
        if (chunk.done) {
          decoder.end();
          break;
        }
        decoder.push(chunk.value);
      }
    } finally {
      if (!result && !failure) reader.cancel().catch(() => undefined);
    }

    if (failure) throw failure;
    if (result) return result;
    throw timeoutError('ended without a reply');
  } finally {
    if (idleTimer) clearTimeout(idleTimer);
  }
}
